import React, { useState, useEffect, useRef } from 'react';
import { executeRecaptcha } from '../services/recaptcha';
import { Link as RouterLink, useNavigate, useLocation } from 'react-router-dom';
import { Input } from './ui/input';
import { authService } from '../services/authService';
import SEOMeta from './SEOMeta';
import {
  Clock,
  Zap,
  Shield,
  TrendingUp,
  Users,
  Bell,
  Eye,
  EyeOff,
  Loader2,
  X,
  CheckCircle2,
  ArrowRight,
  Home,
  UserRound,
} from 'lucide-react';

// ── Left-panel feature list ────────────────────────────────────────────────
const FEATURES = [
  { icon: Clock,      title: '24/7 Availability',     desc: 'Niva never sleeps. Handle tenant requests at 2 AM with the same professional, instant response as 2 PM.'                   },
  { icon: Zap,        title: 'Instant Coordination',  desc: 'Automatically schedules maintenance, coordinates vendors, and keeps everyone in the loop, no phone tag.'                   },
  { icon: Shield,     title: 'Security-First',        desc: 'Bank-level encryption and data handling designed specifically for property managers.'                                        },
  { icon: TrendingUp, title: 'Happier Tenants',       desc: 'Faster response times mean better retention rates and properties that generate consistent returns.'                          },
  { icon: Users,      title: 'Built for the Midwest', desc: 'Optimised for Central Ohio landlords with local vendor networks and market knowledge built in.'                              },
  { icon: Bell,       title: 'Smart Escalations',     desc: 'Only get alerted when it matters. The AI handles routine requests and escalates what truly needs you.'                       },
];

// ── Shake keyframe injected once ──────────────────────────────────────────
const SHAKE_STYLE_ID = 'login-shake-keyframe';
if (!document.getElementById(SHAKE_STYLE_ID)) {
  const style = document.createElement('style');
  style.id = SHAKE_STYLE_ID;
  style.textContent = `
    @keyframes loginShake {
      0%,100% { transform: translateX(0); }
      15%      { transform: translateX(-6px); }
      30%      { transform: translateX(6px); }
      45%      { transform: translateX(-4px); }
      60%      { transform: translateX(4px); }
      75%      { transform: translateX(-2px); }
      90%      { transform: translateX(2px); }
    }
    .login-shake { animation: loginShake 0.35s ease; }
  `;
  document.head.appendChild(style);
}

// ── Remembered-email key ───────────────────────────────────────────────────
const REMEMBER_EMAIL_KEY = 'nivasapro_remembered_email';

// ── Main component ─────────────────────────────────────────────────────────
const Login = ({ onLogin }) => {
  const navigate  = useNavigate();
  const location  = useLocation();

  // ── Initialise email from "remember me" storage ──────────────────────
  const rememberedEmail = localStorage.getItem(REMEMBER_EMAIL_KEY) || '';
  const [formData,      setFormData]      = useState({ email: rememberedEmail, password: '' });
  const [rememberMe,    setRememberMe]    = useState(Boolean(rememberedEmail));
  const [error,         setError]         = useState('');
  const [loading,       setLoading]       = useState(false);
  const [googleLoading,   setGoogleLoading]   = useState(false);
  const [showPassword,    setShowPassword]    = useState(false);
  const [accountTypeStep,  setAccountTypeStep]  = useState(null); // null | 'choose' | 'invite'
  const [socialInviteCode, setSocialInviteCode] = useState('');
  const [modalError,       setModalError]       = useState('');

  // Just-registered success banner
  const [justRegistered, setJustRegistered] = useState(false);

  // Shake state for the form on auth failure
  const [shaking, setShaking] = useState(false);
  const errorDismissTimer = useRef(null);
  const formRef = useRef(null);

  // ── Detect post-registration arrival ────────────────────────────────
  useEffect(() => {
    // Check router state first (preferred), fall back to localStorage flag
    const fromState = location.state?.justRegistered;
    const fromStorage = localStorage.getItem('justRegistered') === 'true';

    if (fromState || fromStorage) {
      setJustRegistered(true);
      localStorage.removeItem('justRegistered');
      // Pre-fill email if passed via router state
      if (location.state?.email) {
        setFormData((prev) => ({ ...prev, email: location.state.email }));
      }
      // Clear router state so a manual refresh doesn't re-show the banner
      window.history.replaceState({}, '');
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-dismiss error after 5 s ────────────────────────────────────
  useEffect(() => {
    if (!error) return;
    clearTimeout(errorDismissTimer.current);
    errorDismissTimer.current = setTimeout(() => setError(''), 5000);
    return () => clearTimeout(errorDismissTimer.current);
  }, [error]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (error) setError('');
  };

  // ── Trigger shake + set error ────────────────────────────────────────
  const showError = (msg) => {
    setError(msg);
    // Remove then re-add the class to re-trigger the animation
    if (formRef.current) {
      formRef.current.classList.remove('login-shake');
      // Force reflow
      void formRef.current.offsetWidth;
      formRef.current.classList.add('login-shake');
      setShaking(true);
      setTimeout(() => {
        formRef.current?.classList.remove('login-shake');
        setShaking(false);
      }, 400);
    }
  };

  // ── Forgot-password: pass email via router state if filled ───────────
  const handleForgotPassword = (e) => {
    e.preventDefault();
    navigate('/forgot-password', {
      state: formData.email.trim() ? { email: formData.email.trim() } : undefined,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    try {
      const recaptchaToken = await executeRecaptcha('login');
      const response = await authService.login({ ...formData, recaptchaToken });

      // Honour "remember me" preference
      if (rememberMe) {
        localStorage.setItem(REMEMBER_EMAIL_KEY, formData.email.trim());
      } else {
        localStorage.removeItem(REMEMBER_EMAIL_KEY);
      }

      localStorage.setItem('token', response.token);
      onLogin(response.user);
      navigate('/niva');
    } catch (err) {
      if (err.response?.status === 403) {
        if (err.response.data?.code === 'EMAIL_NOT_VERIFIED') {
          navigate('/verify-email', { state: { email: err.response.data.email || formData.email } });
          return;
        }
        showError(err.response.data.message || 'Login is currently restricted. Please contact us for early access.');
      } else if (err.response?.status === 401) {
        showError('Incorrect email or password. Please try again.');
      } else {
        showError(err.response?.data?.error || 'Login failed. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setGoogleLoading(true);
    setError('');
    setAccountTypeStep(null);
    setSocialInviteCode('');
    try {
      const result = await authService.signInWithGoogle();
      if (result.needsAccountType) {
        setAccountTypeStep('choose');
        return;
      }
      onLogin(result.user);
      navigate('/niva');
    } catch (err) {
      if (['auth/popup-closed-by-user', 'auth/cancelled-popup-request', 'auth/user-cancelled'].includes(err.code)) return;
      showError(err.response?.data?.error || err.message || 'Google sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleModalClose = async () => {
    setAccountTypeStep(null);
    setSocialInviteCode('');
    setModalError('');
    await authService.logout(); // sign out of Firebase so the popup user isn't half-logged-in
  };

  const handleAccountTypeConfirm = async (userType) => {
    setGoogleLoading(true);
    setModalError('');
    try {
      const result = await authService.completeSocialLogin({
        userType,
        inviteCode: userType === 'Tenant' ? socialInviteCode.trim() : null,
      });
      setAccountTypeStep(null);
      onLogin(result.user);
      navigate('/niva');
    } catch (err) {
      setModalError(err.response?.data?.error || 'Sign-in failed. Please try again.');
    } finally {
      setGoogleLoading(false);
    }
  };

  const canSubmit = formData.email.trim() && formData.password.length > 0 && !loading;

  return (
    <>

    {/* ── Account-type modal — appears after Google popup for brand-new users ── */}
    {accountTypeStep && (
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
        <div className="w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in-95 duration-200">

          {/* Modal header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
            <div className="flex items-center gap-2.5">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-8 w-8" />
              <span className="text-sm font-bold text-gray-900">NivasaPro</span>
            </div>
            <button
              type="button"
              onClick={handleModalClose}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Step 1 — choose role */}
          {accountTypeStep === 'choose' && (
            <div className="px-6 py-6">
              <h3 className="text-xl font-bold text-gray-900 mb-1">How are you joining?</h3>
              <p className="text-sm text-gray-500 mb-6">Choose the account type that fits you. We'll set everything up.</p>

              {modalError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <X className="h-4 w-4 flex-shrink-0" />{modalError}
                </div>
              )}

              <div className="space-y-3">
                <button
                  type="button"
                  onClick={() => handleAccountTypeConfirm('Landlord')}
                  disabled={googleLoading}
                  className="w-full flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 p-4 text-left transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div className="h-11 w-11 rounded-xl bg-cyan-100 group-hover:bg-cyan-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <Home className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Landlord / Property Manager</p>
                    <p className="text-xs text-gray-500 mt-0.5">Manage properties, tenants, and maintenance</p>
                  </div>
                  {googleLoading ? <Loader2 className="h-4 w-4 animate-spin text-gray-400 flex-shrink-0" /> : <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-cyan-500 flex-shrink-0 transition-colors" />}
                </button>

                <button
                  type="button"
                  onClick={() => { setModalError(''); setAccountTypeStep('invite'); }}
                  disabled={googleLoading}
                  className="w-full flex items-center gap-4 rounded-xl border-2 border-gray-200 hover:border-cyan-500 hover:bg-cyan-50 p-4 text-left transition-all duration-150 disabled:opacity-50 disabled:cursor-not-allowed group"
                >
                  <div className="h-11 w-11 rounded-xl bg-cyan-100 group-hover:bg-cyan-200 flex items-center justify-center flex-shrink-0 transition-colors">
                    <UserRound className="h-5 w-5 text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm font-semibold text-gray-900">Tenant</p>
                    <p className="text-xs text-gray-500 mt-0.5">Access your rental info with an invite code</p>
                  </div>
                  <ArrowRight className="h-4 w-4 text-gray-300 group-hover:text-cyan-500 flex-shrink-0 transition-colors" />
                </button>
              </div>
            </div>
          )}

          {/* Step 2 — tenant invite code */}
          {accountTypeStep === 'invite' && (
            <div className="px-6 py-6">
              <button
                type="button"
                onClick={() => { setModalError(''); setAccountTypeStep('choose'); }}
                className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700 mb-5 transition-colors"
              >
                ← Back
              </button>
              <h3 className="text-xl font-bold text-gray-900 mb-1">Enter your invite code</h3>
              <p className="text-sm text-gray-500 mb-5">Your landlord included this in their invitation email.</p>

              {modalError && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2.5 text-sm text-red-700">
                  <X className="h-4 w-4 flex-shrink-0" />{modalError}
                </div>
              )}

              <input
                type="text"
                value={socialInviteCode}
                onChange={e => { setSocialInviteCode(e.target.value.toUpperCase()); setModalError(''); }}
                placeholder="e.g. ABC123"
                className="w-full h-12 px-4 rounded-xl border-2 border-gray-200 focus:border-cyan-500 focus:outline-none text-sm font-mono tracking-widest mb-4 transition-colors"
                autoFocus
              />
              <button
                type="button"
                onClick={() => handleAccountTypeConfirm('Tenant')}
                disabled={!socialInviteCode.trim() || googleLoading}
                className="w-full h-11 rounded-xl bg-cyan-600 hover:bg-cyan-700 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
              >
                {googleLoading ? <><Loader2 className="h-4 w-4 animate-spin" />Verifying…</> : 'Continue as Tenant'}
              </button>
            </div>
          )}

        </div>
      </div>
    )}

    <div className="min-h-screen flex">
      <SEOMeta title="Login" description="Login to your NivasaPro account." canonical="/login" noIndex />

      {/* ════════════════════════════════════════════════════════════════
          LEFT PANEL  (hidden on mobile / tablet)
      ════════════════════════════════════════════════════════════════ */}
      <div
        className="hidden lg:flex lg:w-2/5 flex-col relative overflow-hidden"
        style={{ backgroundColor: '#0F4C5C' }}
      >
        {/* Gradient mesh blobs */}
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-24 -left-24 w-96 h-96 rounded-full opacity-20 blur-3xl" style={{ backgroundColor: '#22D3EE' }} />
          <div className="absolute bottom-0 right-0 w-80 h-80 rounded-full opacity-15 blur-3xl"  style={{ backgroundColor: '#0EA5E9' }} />
          <div className="absolute top-1/2 left-1/3 w-64 h-64 rounded-full opacity-10 blur-2xl"  style={{ backgroundColor: '#6EE7F7' }} />
        </div>

        <div className="relative z-10 flex flex-col h-full px-10 py-10">
          {/* Logo + Home link */}
          <div className="flex items-center justify-between mb-12">
            <div className="flex items-center gap-2.5">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-9 w-9" />
              <div>
                <span className="text-xl font-bold text-white tracking-tight block leading-none">NivasaPro</span>
                <span className="text-xs text-white/50 leading-none">Agentic AI property management</span>
              </div>
            </div>
            <button
              onClick={() => navigate('/')}
              className="text-xs text-white/50 hover:text-white/90 transition-colors flex items-center gap-1"
            >
              ← Home
            </button>
          </div>

          {/* Headline */}
          <div className="mb-10">
            <h1 className="text-3xl xl:text-4xl font-bold text-white leading-tight mb-3">
              Good to have<br />you back.
            </h1>
            <p className="text-white/55 text-sm leading-relaxed">
              Your portfolio is running. Let's check in.
            </p>
          </div>

          {/* Feature list — borderless, editorial */}
          <div className="flex-1 space-y-0">
            {FEATURES.map(({ icon: Icon, title, desc }, i) => (
              <React.Fragment key={title}>
                <div className="flex items-start gap-4 py-4">
                  <div className="h-9 w-9 rounded-lg bg-white/15 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon style={{ height: '1.125rem', width: '1.125rem' }} className="text-white/80" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-white leading-snug mb-0.5">{title}</p>
                    <p className="text-xs text-white/50 leading-relaxed">{desc}</p>
                  </div>
                </div>
                {i < FEATURES.length - 1 && (
                  <div className="flex items-center gap-1.5 px-1">
                    {[...Array(3)].map((_, d) => <div key={d} className="h-px flex-1 bg-white/10" />)}
                  </div>
                )}
              </React.Fragment>
            ))}
          </div>

          {/* Social proof */}
          <div className="mt-8 pt-6 border-t border-white/10">
            <p className="text-xs text-white/40 leading-relaxed">
              Trusted by independent landlords across the Midwest 🏠
            </p>
          </div>
        </div>
      </div>

      {/* ════════════════════════════════════════════════════════════════
          RIGHT PANEL
      ════════════════════════════════════════════════════════════════ */}
      <div className="flex-1 lg:w-3/5 flex flex-col bg-white overflow-y-auto">

        {/* Mobile header bar */}
        <div className="lg:hidden flex items-center justify-between px-5 py-3 border-b bg-white sticky top-0 z-10">
          <div className="flex items-center gap-2">
            <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-7 w-7" />
            <div>
              <span className="text-sm font-bold text-gray-900 leading-none block">NivasaPro</span>
              <span className="text-[11px] text-gray-400 leading-none">Agentic AI property management</span>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="text-xs text-gray-500 hover:text-cyan-600 transition-colors">
            ← Home
          </button>
        </div>

        <div className="flex-1 flex items-center justify-center px-5 py-12 sm:px-8 lg:px-16 xl:px-24">
          <div className="w-full max-w-md">

            {/* Mobile logo mark */}
            <div className="lg:hidden flex justify-center mb-6">
              <img src="/nivasapro.ai-app-Icon.png" alt="NivasaPro" className="h-12 w-12" />
            </div>

            {/* ── 1. POST-REGISTRATION SUCCESS BANNER ───────────────── */}
            {justRegistered && (
              <div className="mb-6 flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3">
                <CheckCircle2 className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-semibold text-emerald-800">Account created!</p>
                  <p className="text-xs text-emerald-700 mt-0.5">Log in below to get started.</p>
                </div>
                <button
                  onClick={() => setJustRegistered(false)}
                  className="text-emerald-400 hover:text-emerald-600 transition-colors flex-shrink-0"
                  aria-label="Dismiss"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            )}

            {/* Heading */}
            <div className="mb-8 text-center lg:text-left">
              <h2 className="text-2xl font-bold text-gray-900 mb-1">Welcome back</h2>
              <p className="text-sm text-gray-500">Login to converse with Niva, your AI Property Agent</p>
            </div>

            {/* Form wrapper — ref for shake animation */}
            <div ref={formRef}>
              <form onSubmit={handleSubmit} className="space-y-5" noValidate>

                {/* Email */}
                <div className="space-y-1.5">
                  <label className="text-sm font-medium text-gray-700" htmlFor="email">
                    Email address
                  </label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="you@example.com"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    disabled={loading}
                    className="h-10"
                  />
                </div>

                {/* Password */}
                <div className="space-y-1.5">
                  <div className="flex items-center justify-between">
                    <label className="text-sm font-medium text-gray-700" htmlFor="password">
                      Password
                    </label>
                    {/* ── 2. FORGOT PASSWORD passes email via state ── */}
                    <a
                      href="/forgot-password"
                      onClick={handleForgotPassword}
                      className="text-xs text-cyan-600 hover:text-cyan-700 hover:underline transition-colors"
                    >
                      Forgot password?
                    </a>
                  </div>
                  <div className="relative">
                    <Input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      value={formData.password}
                      onChange={handleChange}
                      required
                      disabled={loading}
                      className="h-10 pr-10"
                      placeholder="Your password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-3 flex items-center text-gray-400 hover:text-gray-600 transition-colors"
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                </div>

                {/* ── 5. REMEMBER ME ──────────────────────────────────── */}
                <div className="flex items-center gap-2.5">
                  <input
                    id="rememberMe"
                    type="checkbox"
                    checked={rememberMe}
                    onChange={(e) => setRememberMe(e.target.checked)}
                    disabled={loading}
                    className="h-4 w-4 rounded border-gray-300 text-cyan-600 focus:ring-cyan-500 cursor-pointer shrink-0"
                  />
                  <label htmlFor="rememberMe" className="text-xs text-gray-500 cursor-pointer select-none">
                    Remember me on this device
                  </label>
                </div>

                {/* ── 3. INLINE ERROR — red left border, above button ── */}
                <div
                  style={{
                    maxHeight: error ? '80px' : '0px',
                    opacity:   error ? 1 : 0,
                    overflow:  'hidden',
                    transition: 'max-height 0.2s ease, opacity 0.15s ease',
                  }}
                >
                  {error && (
                    <div className="flex items-start gap-2.5 rounded-lg border-l-4 border-red-500 bg-red-50 px-4 py-3 text-sm text-red-700">
                      <X className="h-4 w-4 mt-0.5 flex-shrink-0 text-red-500" />
                      <span className="flex-1">{error}</span>
                      <button
                        type="button"
                        onClick={() => setError('')}
                        className="text-red-400 hover:text-red-600 transition-colors flex-shrink-0"
                        aria-label="Dismiss error"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                  )}
                </div>

                {/* ── 4. LOGIN BUTTON with loading state ──────────────── */}
                <button
                  type="submit"
                  disabled={!canSubmit}
                  className={[
                    'w-full h-11 rounded-lg text-sm font-semibold text-white',
                    'flex items-center justify-center gap-2',
                    'transition-all duration-200',
                    !canSubmit
                      ? 'bg-cyan-300 cursor-not-allowed'
                      : 'bg-cyan-600 hover:bg-cyan-700 active:scale-[0.98] shadow-sm hover:shadow-md',
                  ].join(' ')}
                >
                  {loading ? (
                    <><Loader2 className="h-4 w-4 animate-spin" />Signing in…</>
                  ) : (
                    'Login'
                  )}
                </button>

              </form>
            </div>

            {/* Google sign-in */}
            <div className="relative my-6">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 text-gray-400">or</span>
              </div>
            </div>

            <button
              type="button"
              onClick={handleGoogleSignIn}
              disabled={googleLoading || loading}
              className="w-full h-11 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 active:scale-[0.98] text-sm font-medium text-gray-700 flex items-center justify-center gap-3 transition-all duration-200 shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {googleLoading ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  <svg viewBox="0 0 24 24" className="h-4 w-4 flex-shrink-0" aria-hidden="true">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                  </svg>
                  Continue with Google
                </>
              )}
            </button>

            {/* Sign-up card */}
            <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 px-5 py-4">
              <p className="text-sm font-semibold text-gray-800 mb-0.5">New to NivasaPro?</p>
              <p className="text-xs text-gray-500 mb-3">Join early adopters and lock in 50% off for life.</p>
              <RouterLink
                to="/register"
                className="flex items-center justify-center gap-1.5 w-full h-10 rounded-lg border-2 border-cyan-600 text-cyan-600 text-sm font-semibold hover:bg-cyan-50 transition-colors"
              >
                Create an account <ArrowRight className="h-3.5 w-3.5" />
              </RouterLink>
            </div>

          </div>
        </div>
      </div>
    </div>

    </>
  );
};

export default Login;
